import {
  API_AUTH_TYPE_VALUES,
  API_HTTP_METHOD_VALUES,
  type ApiDeliveryConfig,
  type ApiDeliveryEndpointConfig,
} from '@steerfi/shared/types/jsonb/integration-profile.types';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EndpointsEditor } from './endpoints-editor';
import { KvEditor } from './kv-editor';

interface ApiDeliveryFieldsProps {
  value: ApiDeliveryConfig | undefined;
  onChange: (v: ApiDeliveryConfig) => void;
  /** True while a header key or endpoint name is blank or duplicated. */
  onDuplicateChange?: (hasDuplicate: boolean) => void;
  disabled?: boolean;
}

const emptyConfig = (): ApiDeliveryConfig => ({
  baseUrl: '',
});

const isValidUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

export const ApiDeliveryFields: React.FC<ApiDeliveryFieldsProps> = ({
  value,
  onChange,
  onDuplicateChange,
  disabled,
}) => {
  const cfg = value ?? emptyConfig();
  const isDisabled = disabled ?? false;
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);
  const [headersDuplicate, setHeadersDuplicate] = useState(false);
  const [endpointsDuplicate, setEndpointsDuplicate] = useState(false);
  const onDuplicateChangeRef = useRef(onDuplicateChange);
  onDuplicateChangeRef.current = onDuplicateChange;

  useEffect(() => {
    setBaseUrlError(null);
  }, [cfg.baseUrl]);

  useEffect(() => {
    onDuplicateChangeRef.current?.(headersDuplicate || endpointsDuplicate);
  }, [headersDuplicate, endpointsDuplicate]);

  useEffect(() => {
    return () => onDuplicateChangeRef.current?.(false);
  }, []);

  const setField = <K extends keyof ApiDeliveryConfig>(key: K, val: ApiDeliveryConfig[K]) => {
    onChange({ ...cfg, [key]: val });
  };

  const deleteField = <K extends keyof ApiDeliveryConfig>(key: K) => {
    const next = { ...cfg };
    delete next[key];
    onChange(next);
  };

  const authType = cfg.auth?.type ?? 'none';

  const updateAuthType = (type: string) => {
    if (type === 'none') {
      onChange({ ...cfg, auth: { type: 'none' } });
    } else {
      onChange({
        ...cfg,
        auth: {
          ...(cfg.auth ?? { type: 'none' }),
          type: type as (typeof API_AUTH_TYPE_VALUES)[number],
        },
      });
    }
  };

  const handleTimeoutChange = (raw: string) => {
    if (raw) {
      const n = Number(raw);
      if (!isNaN(n)) setField('timeoutMs', n);
    } else {
      deleteField('timeoutMs');
    }
  };

  const handleEndpointPathChange = (raw: string) => {
    if (raw) setField('endpointPath', raw);
    else deleteField('endpointPath');
  };

  const handleAuthSecretArnChange = (raw: string) => {
    const next = { ...(cfg.auth ?? { type: authType as (typeof API_AUTH_TYPE_VALUES)[number] }) };
    if (raw) next.secretArn = raw;
    else delete next.secretArn;
    onChange({ ...cfg, auth: next });
  };

  const handleApiKeyHeaderChange = (raw: string) => {
    const next = { ...(cfg.auth ?? { type: authType as (typeof API_AUTH_TYPE_VALUES)[number] }) };
    if (raw) next.apiKeyHeader = raw;
    else delete next.apiKeyHeader;
    onChange({ ...cfg, auth: next });
  };

  const handleHeadersChange = (headers: Record<string, string>) => {
    if (Object.keys(headers).length) setField('headers', headers);
    else deleteField('headers');
  };

  const handleEndpointsChange = (endpoints: Record<string, ApiDeliveryEndpointConfig>) => {
    if (Object.keys(endpoints).length) setField('endpoints', endpoints);
    else deleteField('endpoints');
  };

  return (
    <div className="space-y-4 rounded-xl border p-4">
      <p className="text-sm font-medium text-foreground">API Delivery</p>

      <div className="space-y-2">
        <Label htmlFor="api-base-url" className="text-xs">
          Base URL <span className="text-destructive">*</span>
        </Label>
        <Input
          id="api-base-url"
          className="text-xs font-mono"
          disabled={isDisabled}
          placeholder="https://api.provider.com"
          value={cfg.baseUrl}
          aria-invalid={baseUrlError !== null}
          onChange={(e) => {
            setBaseUrlError(null);
            setField('baseUrl', e.target.value);
          }}
          onBlur={(e) => {
            const val = e.target.value;
            if (!val) setBaseUrlError('Base URL is required');
            else if (!isValidUrl(val))
              setBaseUrlError('Must be a valid URL (e.g. https://api.provider.com)');
          }}
        />
        {baseUrlError && <p className="text-xs text-destructive">{baseUrlError}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">Method</Label>
          <Select
            disabled={isDisabled}
            value={cfg.method ?? ''}
            onValueChange={(v) => setField('method', v as (typeof API_HTTP_METHOD_VALUES)[number])}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Default" />
            </SelectTrigger>
            <SelectContent>
              {API_HTTP_METHOD_VALUES.map((m) => (
                <SelectItem key={m} value={m} className="text-xs">
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="api-timeout" className="text-xs">
            Timeout (ms)
          </Label>
          <Input
            id="api-timeout"
            type="number"
            className="h-8 text-xs"
            disabled={isDisabled}
            placeholder="e.g. 30000"
            value={cfg.timeoutMs ?? ''}
            onChange={(e) => handleTimeoutChange(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="api-endpoint-path" className="text-xs">
          Endpoint Path
        </Label>
        <Input
          id="api-endpoint-path"
          className="text-xs font-mono"
          disabled={isDisabled}
          placeholder="/v1/payments/disbursements"
          value={cfg.endpointPath ?? ''}
          onChange={(e) => handleEndpointPathChange(e.target.value)}
        />
      </div>

      <div className="space-y-3 rounded-xl bg-muted/30 p-3">
        <p className="text-xs font-medium text-muted-foreground">Authentication</p>
        <div className="space-y-2">
          <Label className="text-xs">Auth type</Label>
          <Select disabled={isDisabled} value={authType} onValueChange={updateAuthType}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {API_AUTH_TYPE_VALUES.map((t) => (
                <SelectItem key={t} value={t} className="text-xs capitalize">
                  {t === 'api_key'
                    ? 'API Key'
                    : t === 'none'
                      ? 'None'
                      : t.charAt(0).toUpperCase() + t.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {authType !== 'none' && (
          <div className="space-y-2">
            <Label htmlFor="api-auth-arn" className="text-xs">
              Secret ARN
            </Label>
            <Input
              id="api-auth-arn"
              className="text-xs font-mono"
              disabled={isDisabled}
              placeholder="arn:aws:secretsmanager:..."
              value={cfg.auth?.secretArn ?? ''}
              onChange={(e) => handleAuthSecretArnChange(e.target.value)}
            />
          </div>
        )}

        {authType === 'api_key' && (
          <div className="space-y-2">
            <Label htmlFor="api-key-header" className="text-xs">
              API Key header name
            </Label>
            <Input
              id="api-key-header"
              className="text-xs font-mono"
              disabled={isDisabled}
              placeholder="x-api-key"
              value={cfg.auth?.apiKeyHeader ?? ''}
              onChange={(e) => handleApiKeyHeaderChange(e.target.value)}
            />
          </div>
        )}
      </div>

      <KvEditor
        label="Headers"
        disabled={isDisabled}
        value={cfg.headers ?? {}}
        onChange={handleHeadersChange}
        onDuplicateChange={setHeadersDuplicate}
      />

      <EndpointsEditor
        value={cfg.endpoints ?? {}}
        disabled={isDisabled}
        onChange={handleEndpointsChange}
        onDuplicateChange={setEndpointsDuplicate}
      />
    </div>
  );
};
